import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2, Save, Plus, Trash2, Link2, Copy, Check,
  Briefcase, Send, ClipboardCheck, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BRIEF_FIELDS, ASPECT_RATIOS, DELIVERABLE_PLATFORMS, EDIT_LENGTHS, formatEditLength, BRIEF_STATUS_META,
  BRIEF_STATUS_ORDER, emptyDeliverable, deliverableSummary,
} from '../lib/briefHelpers';
import { getBackendUrl } from '../lib/api';
import { CheckboxGroup } from '../components/CheckboxGroup';
import { BriefStatusBadge } from '../components/BriefQuickView';
import { BackButton } from '../components/BackButton';
import { PhoneTitle } from '../components/PhoneHeader';

function DeliverableRow({ deliverable, index, onChange, onRemove }) {
  const d = deliverable;
  const set = (field, value) => onChange(index, { ...d, [field]: value });

  return (
    <div className="border border-line rounded-cards p-4 space-y-3" data-testid={`deliverable-row-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Deliverable {index + 1}</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(index)}
          data-testid={`remove-deliverable-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="space-y-1.5 sm:w-[15%]">
            <Label className="text-xs">Edit length</Label>
            <Select value={d.duration || ''} onValueChange={(v) => set('duration', v)}>
              <SelectTrigger data-testid={`deliverable-length-${index}`}><SelectValue placeholder="Length" /></SelectTrigger>
              <SelectContent>
                {EDIT_LENGTHS.map((len) => <SelectItem key={len} value={len}>{formatEditLength(len)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 flex-1">
            <Label className="text-xs">Edit name / output label</Label>
            <Input value={d.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Hero Film" data-testid={`deliverable-name-${index}`} />
          </div>
          <div className="space-y-1.5 flex-1">
            <Label className="text-xs">Aspect ratios</Label>
            <CheckboxGroup options={ASPECT_RATIOS} value={d.aspect_ratios || []} onChange={(v) => set('aspect_ratios', v)} allowCustom={false} testid={`deliverable-aspect-${index}`} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="space-y-1.5 sm:w-[60%]">
            <Label className="text-xs">Platforms</Label>
            <CheckboxGroup options={DELIVERABLE_PLATFORMS} value={d.platforms || []} onChange={(v) => set('platforms', v)} allowCustom={false} testid={`deliverable-platform-${index}`} />
          </div>
          <div className="space-y-1.5 flex-1">
            <Label className="text-xs">Notes</Label>
            <Input value={d.notes || ''} onChange={(e) => set('notes', e.target.value)} placeholder="Any specifics for this output" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BriefDetailPage() {
  const { briefId } = useParams();
  const navigate = useNavigate();
  const { getBrief, updateBrief, applyBriefResponse, clients, fetchClients } = useAppStore();
  const { hasProjectsPlan, isAdmin, isDemo, checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();

  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shootDatesText, setShootDatesText] = useState('');

  useEffect(() => {
    if (!projectsPlanChecked) checkProjectsPlanAccess();
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getBrief(briefId);
      if (!data.deliverables || data.deliverables.length === 0) {
        data.deliverables = [emptyDeliverable()];
      }
      setBrief(data);
      setShootDatesText((data.shoot_dates || []).join(', '));
    } catch (e) {
      toast.error('Failed to load brief');
      navigate('/briefs');
    } finally {
      setLoading(false);
    }
  }, [briefId, getBrief, navigate]);

  useEffect(() => {
    fetchClients();
    load();
  }, [fetchClients, load]);

  const setField = (key, value) => setBrief((b) => ({ ...b, [key]: value }));

  const handleClientChange = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    setBrief((b) => ({ ...b, client_id: clientId, client: client?.business_name || '', contact_email: '' }));
  };

  const selectedClient = clients.find((c) => c.id === brief?.client_id);

  const addDeliverable = () => setBrief((b) => {
    const list = b.deliverables || [];
    const last = list[list.length - 1];
    const next = last ? { ...last, id: crypto.randomUUID() } : emptyDeliverable();
    return { ...b, deliverables: [...list, next] };
  });
  const updateDeliverable = (i, val) => setBrief((b) => ({ ...b, deliverables: b.deliverables.map((d, idx) => (idx === i ? val : d)) }));
  const removeDeliverable = (i) => setBrief((b) => ({ ...b, deliverables: b.deliverables.filter((_, idx) => idx !== i) }));

  const buildPayload = () => ({
    project_name: brief.project_name,
    client_id: brief.client_id || null,
    client: brief.client || null,
    contact_email: brief.contact_email || null,
    status: brief.status,
    shoot_dates: shootDatesText.split(',').map((s) => s.trim()).filter(Boolean),
    deliverables: brief.deliverables || [],
    ...Object.fromEntries(BRIEF_FIELDS.map((f) => [f.key, brief[f.key] || null])),
  });

  const handleSave = async (silent = false) => {
    if (!brief.project_name?.trim()) {
      toast.error('Project name is required');
      return false;
    }
    setSaving(true);
    try {
      const updated = await updateBrief(briefId, buildPayload());
      setBrief(updated);
      setShootDatesText((updated.shoot_dates || []).join(', '));
      if (!silent) toast.success('Brief saved');
      return true;
    } catch (e) {
      toast.error('Failed to save brief');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = brief ? `${getBackendUrl()}/brief/${brief.share_token}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      // mark as sent if still draft
      if (brief.status === 'draft') {
        const updated = await updateBrief(briefId, { status: 'sent' });
        setBrief((b) => ({ ...b, status: updated.status }));
      }
      toast.success('Client link copied');
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const handleApplyResponse = async () => {
    try {
      const updated = await applyBriefResponse(briefId);
      setBrief(updated);
      setShootDatesText((updated.shoot_dates || []).join(', '));
      toast.success('Client response applied to brief');
    } catch (e) {
      toast.error('Failed to apply response');
    }
  };

  const handleCreateJob = async () => {
    const ok = await handleSave(true);
    if (ok) navigate(`/jobs/new?brief_id=${briefId}`);
  };

  if (!hasProjectsPlan && !isAdmin && !isDemo) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto text-center py-20">
          <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <h1 className="section-title mb-2">Briefs</h1>
          <p className="text-muted-foreground">Briefs are available on the Filmmaker plan.</p>
        </div>
      </Layout>
    );
  }

  if (loading || !brief) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  const response = brief.client_response;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8 pb-24" data-testid="brief-detail-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="title-row gap-3 min-w-0">
            <PhoneTitle title={brief.project_name} onBack={() => navigate('/briefs')} backLabel="Back to briefs" />
            <BackButton onClick={() => navigate('/briefs')} label="Back to briefs" className="max-sm:hidden" data-testid="brief-back-btn" />
            <h1 className="record-title truncate min-w-0 max-sm:hidden">{brief.project_name}</h1>
            <BriefStatusBadge status={brief.status} />
          </div>
          <div className="flex items-center gap-2">
            {brief.job_id ? (
              <Button variant="outline" onClick={() => navigate(`/jobs/${brief.job_id}`)} data-testid="view-linked-job-btn">
                <Briefcase className="w-4 h-4" /> View job
              </Button>
            ) : (
              <Button variant="outline" onClick={handleCreateJob} data-testid="create-job-from-brief-btn">
                <Briefcase className="w-4 h-4" /> Create job
              </Button>
            )}
            <Button onClick={() => handleSave(false)} disabled={saving} data-testid="save-brief-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </Button>
          </div>
        </div>

        {/* Client response review */}
        {response && (
          <Card className={response.reviewed ? '' : 'bg-ochre-soft'}>
            <CardHeader>
              <p className="eyebrow">from the client</p>
              <CardTitle className="section-title flex items-center gap-2">
                <ClipboardCheck className={`w-[18px] h-[18px] ${response.reviewed ? '' : 'text-ochre'}`} />
                Client response {response.reviewed ? '(applied)' : '(needs review)'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                Submitted by <span className="font-medium text-foreground">{response.submitted_by_name || response.submitted_by_email || 'the client'}</span>
                {response.submitted_at ? ` on ${new Date(response.submitted_at).toLocaleString()}` : ''}
              </p>
              {response.production?.shoot_dates?.length > 0 && (
                <div>
                  <p className="font-medium">Shoot dates</p>
                  <p className="text-muted-foreground">{response.production.shoot_dates.join(', ')}</p>
                </div>
              )}
              {BRIEF_FIELDS.filter((f) => f.clientFacing && response.production?.[f.key]).map((f) => (
                <div key={f.key}>
                  <p className="font-medium">{f.label}</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{response.production[f.key]}</p>
                </div>
              ))}
              {response.deliverables?.length > 0 && (
                <div>
                  <p className="font-medium mb-1">Deliverables ({response.deliverables.length})</p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                    {response.deliverables.map((d) => (
                      <li key={d.id}>{deliverableSummary(d) || 'Untitled'}{d.quantity > 1 ? ` (x${d.quantity})` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!response.reviewed && (
                <Button size="sm" onClick={handleApplyResponse} data-testid="apply-response-btn">
                  <Check className="w-4 h-4" /> Apply to brief
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Setup */}
        <Card>
          <CardHeader><p className="eyebrow">the basics</p><CardTitle className="section-title">Setup</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Project name *</Label>
                <Input value={brief.project_name || ''} onChange={(e) => setField('project_name', e.target.value)} data-testid="brief-detail-project-name" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={brief.status} onValueChange={(v) => setField('status', v)}>
                  <SelectTrigger data-testid="brief-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BRIEF_STATUS_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>{BRIEF_STATUS_META[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={brief.client_id || ''} onValueChange={handleClientChange}>
                  <SelectTrigger data-testid="brief-detail-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.business_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contact</Label>
                <Select
                  value={brief.contact_email || ''}
                  onValueChange={(v) => setField('contact_email', v)}
                  disabled={!selectedClient || !(selectedClient.contacts || []).length}
                >
                  <SelectTrigger data-testid="brief-contact-select"><SelectValue placeholder="Select a contact" /></SelectTrigger>
                  <SelectContent>
                    {(selectedClient?.contacts || []).map((ct) => (
                      <SelectItem key={ct.email} value={ct.email}>{ct.name ? `${ct.name} — ${ct.email}` : ct.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Shoot dates</Label>
              <Input value={shootDatesText} onChange={(e) => setShootDatesText(e.target.value)} placeholder="Comma-separated, e.g. 12 Aug, 13 Aug" />
            </div>
          </CardContent>
        </Card>

        {/* Share link */}
        <Card>
          <CardHeader><p className="eyebrow">for the client</p><CardTitle className="section-title flex items-center gap-2"><Link2 className="w-[18px] h-[18px]" /> Client form link</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Send this link to your client so they can fill in the brief. Their answers come back here for your review.</p>
            <div className="flex items-center gap-2">
              <Input readOnly value={shareUrl} className="text-xs" data-testid="brief-share-url" />
              <Button variant="outline" onClick={copyLink} data-testid="copy-brief-link-btn">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
              </Button>
              <Button variant="outline" onClick={() => window.open(shareUrl, '_blank')} data-testid="preview-brief-form-btn">
                <Send className="w-4 h-4" /><span className="hidden sm:inline">Preview</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Production details */}
        <Card>
          <CardHeader><p className="eyebrow">the shoot</p><CardTitle className="section-title">Production details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {BRIEF_FIELDS.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label className="flex items-center gap-2">
                  {f.label}
                  {!f.clientFacing && <Badge variant="outline" className="text-[11px] font-normal">Internal only</Badge>}
                </Label>
                <Textarea
                  value={brief[f.key] || ''}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={3}
                  data-testid={`brief-field-${f.key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Deliverables */}
        <Card>
          <CardHeader>
            <p className="eyebrow">after the shoot</p>
            <CardTitle className="section-title">Post-production deliverables</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {(brief.deliverables || []).map((d, i) => (
              <DeliverableRow key={d.id} deliverable={d} index={i} onChange={updateDeliverable} onRemove={removeDeliverable} />
            ))}
            <Button onClick={addDeliverable} data-testid="add-deliverable-btn">
              <Plus className="w-4 h-4" /> Add deliverable
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
